/**
 * "Raporty" and "Webinary" — the gated expert materials, served from the CMS
 * and opening the same lead gate as the home page's magnet cards.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import { applyPageSeo } from "../core/seo.js";
import { contentSeed } from "../data/seed.js";
import { breadcrumbs, pill } from "../components/common.js";
import { newsletterStrip } from "../components/overlays.js";
import type { Resource, Webinar } from "../data/types.js";

export async function resourcesView(ctx: AppContext): Promise<HTMLElement> {
	const copy = ctx.copy();
	const [resources] = await Promise.all([ctx.gateway.resources()]);

	const title = copy.resources.title;
	applyPageSeo(ctx, { title: `${title} — ${copy.brand.name}`, description: copy.home.magnetsLead });

	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const resource of resources) grid.appendChild(resourceCard(ctx, resource));

	return el(
		"div",
		{ class: "mb-view mb-view--resources" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: title }]),
				el("h1", { class: "mb-pagehead__title" }, copy.home.magnetsTitle),
				el("p", { class: "mb-pagehead__lead" }, copy.home.magnetsLead),
				grid,
				newsletterStrip(ctx),
			),
		),
	);
}

/**
 * Resource tile. The gate modal is keyed by the magnet's index in the seed, so
 * match on slug; a CMS resource with no seed counterpart still converts through
 * the contact page rather than opening a gate it has no copy for.
 */
function resourceCard(ctx: AppContext, resource: Resource): HTMLElement {
	const magnets = contentSeed(ctx.locale()).magnets;
	const index = magnets.findIndex((magnet) => magnet.slug === resource.slug);
	const cta = magnets[index]?.cta ?? ctx.t("common.readMore");

	if (index === -1) {
		return el(
			"a",
			{ class: "mb-card mb-card--magnet", href: ctx.href("contact") },
			el("span", { class: "mb-pill mb-pill--accent" }, resource.kind),
			el("span", { class: "mb-card__title" }, resource.title),
			el("span", { class: "mb-card__text" }, resource.description),
			el("span", { class: "mb-card__more" }, `${cta} →`),
		);
	}

	return el(
		"button",
		{ class: "mb-card mb-card--magnet", type: "button", onClick: () => ctx.openGate(index) },
		el("span", { class: "mb-pill mb-pill--accent" }, resource.kind),
		el("span", { class: "mb-card__title" }, resource.title),
		el("span", { class: "mb-card__text" }, resource.description),
		el("span", { class: "mb-card__more" }, `${cta} →`),
	);
}

export async function webinarsView(ctx: AppContext): Promise<HTMLElement> {
	const copy = ctx.copy();
	const webinars = await ctx.gateway.webinars();

	const title = copy.resources.webinarsTitle;
	applyPageSeo(ctx, { title: `${title} — ${copy.brand.name}`, description: copy.home.magnetsLead });

	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const webinar of webinars) grid.appendChild(webinarCard(ctx, webinar));

	return el(
		"div",
		{ class: "mb-view mb-view--webinars" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: title }]),
				el("h1", { class: "mb-pagehead__title" }, title),
				el("p", { class: "mb-pagehead__lead" }, copy.home.magnetsLead),
				grid.childElementCount > 0
					? grid
					: el("p", { class: "mb-searchresults__empty" }, copy.resources.noWebinars),
			),
		),
	);
}

function webinarCard(ctx: AppContext, webinar: Webinar): HTMLElement {
	const starts = new Date(webinar.startsAt);
	const isUpcoming = starts.getTime() >= Date.now();
	const copy = ctx.copy();
	const dateLabel = new Intl.DateTimeFormat(ctx.locale(), {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(starts);

	return el(
		"a",
		{ class: "mb-card mb-card--insight", href: ctx.href("contact") },
		pill(isUpcoming ? copy.resources.upcoming : copy.resources.recording, "accent"),
		el("h3", { class: "mb-card__title" }, webinar.title),
		el("p", { class: "mb-card__text" }, webinar.description),
		el("span", { class: "mb-card__meta" }, dateLabel),
	);
}
